import React, { FC, useState, useEffect } from "react";
import useCustomerContext from "Contexts/customer-context";
import Page from "Shared/components/page/page";
import { useTranslation } from "Shared/util/custom-hooks";
import { PayoutInformationAccount } from "Shared/util/types/api";
import { getPayoutInformation } from "Shared/util/web-api";
import { useComplianceSummaryQuery } from "Shared/util/web-api-hooks";
import AccountplanSettingsCard from "./settings-cards/accountplan-settings-card";
import { ContractSettingsCard } from "./settings-cards/contract-settings-card";
import KycSettingsCard from "./settings-cards/kyc-settings-card";
import SalesMethodsSettingsCard from "./settings-cards/sales-methods-settings-card";
import { PayoutInformationSettingsCard } from "./settings-cards/payout-information-settings-card";
import styles from "./company-settings-page.css";

export const CompanySettingsPage: FC = () => {
    const [complianceSummary, loadingComplianceSummary] =
        useComplianceSummaryQuery();
    const { updateCustomerStatus, loadingCustomerData } = useCustomerContext();
    const t = useTranslation((x) => x.company_settings);

    const [accounts, setAccounts] = useState<PayoutInformationAccount[] | null>(
        null,
    );

    useEffect(() => {
        getPayoutInformation()
            .then((result) => setAccounts(result.accounts ?? []))
            .catch(() => setAccounts([]));
    }, []);

    useEffect(() => {
        if (complianceSummary) {
            updateCustomerStatus(complianceSummary.status);
        }
    }, [complianceSummary, updateCustomerStatus]);

    const { signatories, status } = complianceSummary || {};
    const { kycCompleted, kycSupported, signatoriesCompleted } = status || {};

    const isLoading =
        loadingComplianceSummary || loadingCustomerData || accounts === null;

    return (
        <Page loading={isLoading} narrowWidth title={t.company_settings}>
            {!!signatories?.length && (
                <ContractSettingsCard
                    className={styles.settingsCardContainer}
                    completed={!!signatoriesCompleted}
                    signatories={signatories}
                />
            )}
            <PayoutInformationSettingsCard
                accounts={accounts ?? []}
                className={styles.settingsCardContainer}
            />
            {kycSupported && (
                <KycSettingsCard
                    className={styles.settingsCardContainer}
                    completed={!!kycCompleted}
                />
            )}
            <SalesMethodsSettingsCard
                className={styles.settingsCardContainer}
            />
            <AccountplanSettingsCard className={styles.settingsCardContainer} />
        </Page>
    );
};
